import { ObstacleType } from '../../obstacles/ObstacleType';

/**
 * Playability check for generated obstacle sequences. Every obstacle row is
 * validated against the previous one before it is accepted – the generator
 * must never produce physically impossible combinations.
 *
 * The model is intentionally conservative: it assumes the player may be in
 * ANY safe lane of the previous row and must still reach a safe lane of the
 * next row in time.
 */
export interface RowCandidate {
  /** Track distance of the row. */
  readonly distance: number;
  /** Obstacle type per lane, null = free lane. */
  readonly lanes: readonly (ObstacleType | null)[];
}

export interface RowHistory {
  readonly distance: number;
  readonly safeLanes: readonly number[];
  /** Every safe lane required a double jump – the player is airborne for long. */
  readonly forcedDoubleJump: boolean;
  /** Requirement per lane of that row – a slide can simply be held into the next slide row. */
  readonly lanes?: readonly (ObstacleType | null)[];
  /**
   * Walls between the lanes (walk-in pipe, platform side) ended here: lane changes are
   * possible only from this distance on, even if the row lies further back.
   */
  readonly laneChangesFrom?: number;
}

export interface SequenceContext {
  readonly laneChangeDuration: number;
  readonly reactionTime: number;
  /** Longest airborne time (ground jump + double jump), seconds. */
  readonly maxAirTime: number;
  readonly speedAt: (distance: number) => number;
}

export interface SequenceRule {
  readonly id: string;
  /** Returns a violation message or null. */
  check(row: RowCandidate, safeLanes: readonly number[], previous: RowHistory | null, ctx: SequenceContext): string | null;
}

export interface ValidationResult {
  readonly ok: boolean;
  readonly violations: readonly string[];
  readonly history: RowHistory;
}

const PASSABLE_BY_ACTION: ReadonlySet<ObstacleType> = new Set([ObstacleType.Jump, ObstacleType.Slide, ObstacleType.DoubleJump]);

/**
 * Two requirements for the same lane on the same decision line – e.g. a terrain
 * crossbar (slide) and a material stack under it, or a pit and an obstacle.
 * Different actions at once (jump AND slide) cannot be done: the lane is blocked.
 */
export function combineLaneTypes(a: ObstacleType | null, b: ObstacleType | null): ObstacleType | null {
  if (a === null) return b;
  if (b === null || a === b) return a;
  if (a === ObstacleType.LaneChange || b === ObstacleType.LaneChange) return ObstacleType.LaneChange;
  const jumps = new Set([a, b]);
  if (jumps.has(ObstacleType.Jump) && jumps.has(ObstacleType.DoubleJump)) return ObstacleType.DoubleJump;
  return ObstacleType.LaneChange;
}

/** Lane-wise combination of two rows on the same decision line (terrain row + obstacle row). */
export function mergeRowLanes(a: readonly (ObstacleType | null)[], b: readonly (ObstacleType | null)[]): (ObstacleType | null)[] {
  return Array.from({ length: Math.max(a.length, b.length) }, (_, lane) => combineLaneTypes(a[lane] ?? null, b[lane] ?? null));
}

/** Lanes the player can get through (free or passable with jump/slide/double jump). */
export function safeLanesOf(row: RowCandidate): number[] {
  const safe: number[] = [];
  row.lanes.forEach((type, lane) => {
    if (type === null || PASSABLE_BY_ACTION.has(type)) safe.push(lane);
  });
  return safe;
}

function timeBetween(row: RowCandidate, previous: RowHistory, ctx: SequenceContext): number {
  return (row.distance - previous.distance) / Math.max(ctx.speedAt(row.distance), 0.001);
}

/** Time for lane changes before the row: from the previous row, or from the end of walls between the lanes. */
function laneChangeTime(row: RowCandidate, previous: RowHistory, ctx: SequenceContext): number {
  const from = Math.max(previous.distance, previous.laneChangesFrom ?? -Infinity);
  return (row.distance - from) / Math.max(ctx.speedAt(row.distance), 0.001) - ctx.reactionTime;
}

/** Staying in the lane needs no new reaction: nothing to do there, or a slide that is simply held. */
function continuesWithoutReaction(row: RowCandidate, previous: RowHistory, lane: number): boolean {
  const next = row.lanes[lane] ?? null;
  return next === null || (next === ObstacleType.Slide && previous.lanes?.[lane] === ObstacleType.Slide);
}

export const ROW_HAS_SAFE_LANE: SequenceRule = {
  id: 'ROW_HAS_SAFE_LANE',
  check: (_row, safeLanes) => (safeLanes.length > 0 ? null : 'row blocks every lane'),
};

/**
 * From every lane the player may be in after the previous row, a safe lane of this row
 * must be reachable: changing lanes needs reaction + lane-change time; staying needs the
 * reaction time for a new action – none if the lane is free or a slide is held on.
 */
export const SAFE_LANE_REACHABLE: SequenceRule = {
  id: 'SAFE_LANE_REACHABLE',
  check: (row, safeLanes, previous, ctx) => {
    if (!previous) return null;
    const reaction = timeBetween(row, previous, ctx) - ctx.reactionTime;
    const available = laneChangeTime(row, previous, ctx);
    for (const from of previous.safeLanes) {
      const reachable = safeLanes.some((to) =>
        to === from
          ? reaction >= 0 || continuesWithoutReaction(row, previous, from)
          : Math.abs(from - to) * ctx.laneChangeDuration <= available,
      );
      if (!reachable) return `lane ${from} cannot reach a safe lane in ${available.toFixed(2)} s`;
    }
    return null;
  },
};

/**
 * After a forced double jump the air jump is spent: while still airborne the
 * player needs a FREE lane – reachable in time from every lane they could be in.
 */
export const AIRTIME_RECOVERY: SequenceRule = {
  id: 'AIRTIME_RECOVERY',
  check: (row, safeLanes, previous, ctx) => {
    if (!previous?.forcedDoubleJump) return null;
    const elapsed = timeBetween(row, previous, ctx);
    if (elapsed >= ctx.maxAirTime) return null;
    const available = laneChangeTime(row, previous, ctx);
    const freeLanes = safeLanes.filter((lane) => row.lanes[lane] === null);
    for (const from of previous.safeLanes) {
      const reachable = freeLanes.some((to) => Math.abs(from - to) * ctx.laneChangeDuration <= available);
      if (!reachable) return `lane ${from}: no free lane reachable while still airborne from a forced double jump`;
    }
    return null;
  },
};

export const DEFAULT_SEQUENCE_RULES: readonly SequenceRule[] = [ROW_HAS_SAFE_LANE, SAFE_LANE_REACHABLE, AIRTIME_RECOVERY];

export class SequenceValidator {
  constructor(
    private readonly ctx: SequenceContext,
    private readonly rules: readonly SequenceRule[] = DEFAULT_SEQUENCE_RULES,
  ) {}

  validate(row: RowCandidate, previous: RowHistory | null): ValidationResult {
    const safeLanes = safeLanesOf(row);
    const violations: string[] = [];
    for (const rule of this.rules) {
      const violation = rule.check(row, safeLanes, previous, this.ctx);
      if (violation) violations.push(`${rule.id}: ${violation}`);
    }
    return {
      ok: violations.length === 0,
      violations,
      history: {
        distance: row.distance,
        safeLanes,
        forcedDoubleJump: safeLanes.length > 0 && safeLanes.every((lane) => row.lanes[lane] === ObstacleType.DoubleJump),
        lanes: row.lanes,
      },
    };
  }

  /**
   * Walls between the lanes end at `distance` (walk-in pipe, platform side): lane changes
   * for the next row only count from there.
   */
  static wallsEnd(history: RowHistory | null, distance: number): RowHistory | null {
    if (!history) return null;
    return { ...history, laneChangesFrom: Math.max(history.laneChangesFrom ?? -Infinity, distance) };
  }
}
